// Notification wrapper — reusable across intranet and editor.
// Reads `root.notifs` array (caller populates via listNotifications).
//
// ⚠️ DELIBERATE DUPLICATE, not the canonical copy (opus lane, 2026-08-19,
// tickets/opus.md census H2). `shared/components/Notifications.js` now
// carries its OWN local `NotificationWrapper` — the canonical one every
// OTHER consumer (canvas, marketplace, docs, analytics, preview) actually
// gets, since a cross-package top-level re-export of THIS file's version
// never resolved for a consumer declaring `shared` without `workspace-shell`
// (frank always stubs it regardless of declaration — FRANK-PAGES-BAG-1,
// tickets/done.md). This copy stays HERE, unchanged, because it is
// project-LOCAL to the shell — which already wins over any shared-library
// merge for the shell's own render tree, so the shell was never actually
// broken by the cross-package stub. Redirecting this file to import
// `@symbo-ls/workspace-shared`'s copy instead would add a NEW cross-package
// edge on the shell's OWN `smbls push` (frank-published despite
// `bundler: parcel`), risking the identical stub trap this relocation just
// fixed for `shared` — not worth it for a component this small. Keep the
// two bodies in sync by hand if either changes.
//
// NOTE: this file used to also export a `NotificationBell` here — an older
// duplicate (no `activeHighlight`) of the canonical bell in
// packages/workspace/components/NotificationBell.js. Removed 2026-07-27.
// Grepped the whole monorepo for other consumers of collab/index.js's
// NotificationBell export — none — so it was genuinely dead, not merely
// shadowed.
//
// NotificationPanel ALSO used to be defined here — an older, divergent
// duplicate (no `extends: 'CommonCard'`, 360x480, no All/Unread filter, its
// own `_markReadOptimistic` / `_markAllReadOptimistic` writing straight to
// ./queries.js) of the canonical panel in
// packages/workspace/components/NotificationBell.js. Both were reachable at
// once: this collab copy rendered exclusively in canvas's standalone bundle
// (links shared, never workspace), while the workspace copy shadowed it in
// the workspace shell (project components barrel wins over the shared-
// library merge) — two different panels behind one key. Removed 2026-07-27:
// the canonical (newer) definition moved to
// shared/components/Notifications.js as the SOLE copy, reusing this
// package's `markNotificationRead` / `markAllNotificationsRead` el.call
// registry (now also promoted to shared/functions so canvas resolves it
// too) instead of the optimistic-write helpers that used to live here.
// Loads root.notifs once a real recipient email is known, and re-loads if it
// changes (sign-out/sign-in in the same tab). Guarding on `el._loadedFor ===
// email` (not a bare "already ran" boolean) is the fix for a real bug found
// live 2026-07-30 verifying the PersonPopover Ping action: the previous guard
// set `el._loaded = true` UNCONDITIONALLY before checking `currentUserEmail`,
// so if this component's first (and, absent a subscription, likely ONLY)
// onRender fired before auth hydration finished — routine on a fresh boot —
// it saw an empty email, bailed, and never got another chance: root.notifs
// stayed permanently empty for the rest of the session even though the
// server had real rows (confirmed: a direct `workspaceProject.notifications`
// list call returned data the bell/panel never saw). Pairing `onInit` +
// `onUpdate` with `subscribeTo: ['currentUserEmail']` mirrors the established
// PendingInvitesBlock pattern (components/PersonCard.js) for "load once a
// late-hydrating root key is ready, retry on the next update".
const _loadNotifs = (el, s) => {
  const email = s.root?.currentUserEmail
  if (!email || el._loadedFor === email) return
  el._loadedFor = email
  ;(async () => {
    const { listNotifications } = await import('./queries.js')
    const { data } = await listNotifications({ recipientEmail: email, limit: 50 })
    const rows = (data || []).map((n) => ({
      ...n,
      createdAt: n.created_at,
      read: !!n.read
    }))
    s.root.update({ notifs: rows })
  })()
}

export const NotificationWrapper = {
  position: 'relative',
  subscribeTo: ['currentUserEmail'],

  onInit: (el, s) => _loadNotifs(el, s),
  onUpdate: (el, s) => _loadNotifs(el, s),

  // Panel is dropdown-layer content now (the bell opens it via
  // openDropdown) — mounting it here would render a second, in-flow copy.
  NotificationBell: {}
}
